import { useState } from "react";
import { Plus, Trash, PencilSimple, Key } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, useApi, inr, fmtDate } from "../lib/api";
import { useT } from "../lib/i18n";
import { PageHeader, GlassCard, Modal, Field, Loader, Empty, Table, Badge, CredentialsDialog, ExportButtons } from "../components/kit";
import { PersonPhoto, PhotoEditor } from "../components/Photo";

const BLANK = { name: "", email: "", phone: "", designation: "Teacher", subject: "", qualification: "", joining_date: "", salary: "",
  gender: "", dob: "", social_category: "", appointment_type: "", teacher_type: "", professional_qualification: "" };
const SELECTS = { gender: ["Male", "Female", "Other"], social_category: ["General", "SC", "ST", "OBC"], appointment_type: ["Regular", "Contract", "Part-time"],
  teacher_type: ["PRT", "TGT", "PGT", "Head Teacher", "Non-teaching"] };

function StaffForm({ initial, onClose, onSaved, onPhoto }) {
  const { t } = useT();
  const editing = !!initial?.id;
  const [ph, setPh] = useState({ has_photo: initial?.has_photo, photo_version: initial?.photo_version });
  const [f, setF] = useState(editing ? { ...BLANK, ...initial } : BLANK);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, salary: Number(f.salary || 0) };
    try {
      if (editing) {
        const { name, phone, designation, subject, qualification, joining_date, salary, gender, dob, social_category, appointment_type, teacher_type, professional_qualification } = body;
        await api.put(`/staff/${initial.id}`, { name, phone, designation, subject, qualification, joining_date, salary, gender, dob, social_category, appointment_type, teacher_type, professional_qualification });
        toast.success("Staff updated");
        onSaved(null);
      } else {
        const r = await api.post("/staff", body);
        toast.success(`${r.data.staff.name} added`);
        onSaved(r.data.credentials);
      }
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  const fields = [["name", t("Name"), "text", true], ["email", t("Email"), "email", !editing], ["phone", t("Phone")], ["designation", t("Designation")],
    ["subject", t("Subject")], ["qualification", "Qualification"], ["professional_qualification", "Professional qualification"], ["joining_date", "Joining date", "date"], ["dob", "Date of birth", "date"], ["salary", `${t("Salary")} (₹/month)`, "number"]];
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={editing ? t("Edit") : t("Add staff")} wide testid="staff-form-dialog"
      description={editing ? "" : "A teacher login is created automatically."}>
      {editing && (
        <div className="mb-5">
          <PhotoEditor kind="staff" person={{ ...initial, ...ph }} testid="staff-photo"
            onChange={(has) => { setPh({ has_photo: has, photo_version: String(Date.now()) }); onPhoto(); }} />
        </div>
      )}
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.filter(([k]) => !(editing && k === "email")).map(([k, l, type = "text", req]) => (
          <Field key={k} label={l}><input className="field" type={type} required={!!req} value={f[k] ?? ""} onChange={set(k)} data-testid={`staff-${k}-input`} /></Field>
        ))}
        {Object.entries(SELECTS).map(([k, opts]) => (
          <Field key={k} label={{ gender: "Gender", social_category: "Social category", appointment_type: "Nature of appointment", teacher_type: "Teacher type" }[k]}>
            <select className="field" value={f[k] || ""} onChange={set(k)} data-testid={`staff-${k}-select`}>
              <option value="">—</option>{opts.map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>
        ))}
        <button className="btn-primary sm:col-span-2" data-testid="staff-submit-btn">{t("Save")}</button>
      </form>
    </Modal>
  );
}

export default function Staff() {
  const { t } = useT();
  const { data, loading, reload } = useApi("/staff");
  const [form, setForm] = useState(null);
  const [creds, setCreds] = useState(null);
  const act = async (fn, msg) => {
    try {
      const r = await fn();
      if (msg) toast.success(msg);
      reload();
      return r;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="People" title={t("Staff")} subtitle={`${data?.length ?? 0} team members`}
        actions={<><ExportButtons entity="staff" /><button className="btn-primary" onClick={() => setForm({})} data-testid="add-staff-btn"><Plus size={16} weight="bold" /> {t("Add staff")}</button></>} />
      <GlassCard className="p-4 sm:p-6">
        {loading ? <Loader /> : !data?.length ? <Empty /> : (
          <Table testid="staff-table">
            <thead><tr><th>{t("Name")}</th><th>{t("Designation")}</th><th>{t("Subject")}</th><th>{t("Email")}</th><th>{t("Phone")}</th><th>{t("Salary")}</th><th>Joined</th><th>{t("Status")}</th><th></th></tr></thead>
            <tbody>
              {data.map((s) => (
                <tr key={s.id} data-testid={`staff-row-${s.email}`}>
                  <td><span className="flex items-center gap-3 font-semibold text-white"><PersonPhoto kind="staff" person={s} className="h-8 w-8 text-[10px]" testid={`staff-photo-${s.id}`} />{s.name}</span></td><td>{s.designation}</td><td>{s.subject || "—"}</td><td className="text-zinc-400">{s.email}</td>
                  <td>{s.phone || "—"}</td><td>{inr(s.salary)}</td><td>{fmtDate(s.joining_date)}</td>
                  <td><Badge status={s.active !== false ? "paid" : "draft"}>{s.active !== false ? "Active" : "Inactive"}</Badge></td>
                  <td className="text-right space-x-1">
                    <button className="btn-ghost !p-2" title="Edit" onClick={() => setForm(s)} data-testid={`staff-edit-${s.id}`}><PencilSimple size={15} /></button>
                    <button className="btn-ghost !p-2" title="Reset login" onClick={async () => { const r = await act(() => api.post(`/staff/${s.id}/credentials`)); if (r) setCreds(r.data); }} data-testid={`staff-reset-${s.id}`}><Key size={15} /></button>
                    <button className="btn-ghost !p-2 !text-rose-300" title="Delete" onClick={() => window.confirm(`Remove ${s.name}?`) && act(() => api.delete(`/staff/${s.id}`), "Removed")} data-testid={`staff-delete-${s.id}`}><Trash size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </GlassCard>
      {form && <StaffForm initial={form.id ? form : null} onClose={() => setForm(null)} onPhoto={reload} onSaved={(c) => { setForm(null); reload(); if (c) setCreds(c); }} />}
      <CredentialsDialog creds={creds} onClose={() => setCreds(null)} />
    </div>
  );
}
